import Vue from "vue";
import createAuth0Client from "@auth0/auth0-spa-js";
import userAuthService from "../api/userAuthorizationService";
import tenantConfigService from "../api/tenantConfigService";


const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

let instance;

export const getInstance = () => instance;

export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  ...options
}) => {
  if (instance) return instance;

  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null,
        permissions: [],
        meetingTypePermissions: [],
        roles_data: {},
        isMultipleTenant: false,
        companyTitles: [],
      };
    },
    methods: {
      async loginWithPopup(options, config) {
        this.popupOpen = true;

        try {
          await this.auth0Client.loginWithPopup(options, config);
          const isAuthenticated = await this.auth0Client.isAuthenticated();
          const user = await this.auth0Client.getUser();
          if (user && isAuthenticated) {
            const tenants = user[process.env.VUE_APP_TENANT_KEY];
            this.isMultipleTenant = await this.isTenantSelectionPopup(tenants);
            if (!this.isMultipleTenant) {
              const { general_permission, meeting_type_permissions, roles_data } = await this.getUserAccessPermissions(user.email)
              this.permissions = general_permission
              this.meetingTypePermissions = meeting_type_permissions
              this.roles_data = roles_data
            }
            this.isAuthenticated = isAuthenticated

          }
          this.error = null;
        } catch (e) {
          console.error(e);
          this.error = e;
        } finally {
          this.popupOpen = false;
        }
      },
      async handleRedirectCallback() {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          const user = await this.auth0Client.getUser();
          const isAuthenticated = true;
          if (user && isAuthenticated) {
            let userDetails = {};
            const tenants = user[process.env.VUE_APP_TENANT_KEY];
            this.isMultipleTenant = await this.isTenantSelectionPopup(tenants);
            if (!this.isMultipleTenant) {
              userDetails = await this.getLoggedInUserDetails()
              const { general_permission, meeting_type_permissions, roles_data } = await this.getUserAccessPermissions(user.email)
              this.permissions = general_permission
              this.meetingTypePermissions = meeting_type_permissions
              this.roles_data = roles_data
            }
            this.isAuthenticated = isAuthenticated
            this.user = { ...user, ...userDetails };
            const hasUserDetails =
              userDetails &&
              typeof userDetails === 'object' &&
              !Array.isArray(userDetails) &&
              Object.keys(userDetails).length > 0;
            if (hasUserDetails) {
              await this.updateTenantListWithHistory();
            }
            if (userDetails.first_name && userDetails.first_name !== "") {
              this.user.tenantUserName = `${userDetails.first_name} ${userDetails.last_name}`
            }
          }
          this.error = null;
        } catch (e) {
          this.error = e;
        } finally {
          this.loading = false;
        }
      },
      async loadPermissions(email) {
        const { general_permission, meeting_type_permissions, roles_data } = await this.getUserAccessPermissions(email)
        this.permissions = general_permission
        this.meetingTypePermissions = meeting_type_permissions
        this.roles_data = roles_data
        await this.updateUserPermissionsDetails()
        const hasUserDetails =
          this.user &&
          typeof this.user === 'object' &&
          !Array.isArray(this.user) &&
          Object.keys(this.user).length > 0;
        if (hasUserDetails) {
          await this.updateTenantListWithHistory();
        }
      },
      resetAllPermissions() {
        this.permissions = []
        this.meetingTypePermissions = []
      },
      loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o);
      },
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o);
      },
      getTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o);
      },
      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      async logout(o) {
        await userAuthService.setActivityLog("logout")
        return this.auth0Client.logout(o);
      },
      async getUserAccessPermissions(id) {
        const allPermission = await userAuthService.getUserAccessPermissions(id);
        return allPermission;
      },
      async getLoggedInUserDetails() {
        return await userAuthService.getLoggedInUserDetails();
      },
      async updateUserPermissionsDetails() {
        let userDetails = await this.getLoggedInUserDetails()
        if (userDetails.first_name && userDetails.first_name !== "") {
          userDetails.name = `${userDetails.first_name} ${userDetails.last_name}`
          userDetails.tenantUserName = `${userDetails.first_name} ${userDetails.last_name}`
        }
        this.user = { ...this.user, ...userDetails };
        return userDetails
      },
      async loadMeetingTypePermissions(email) {
        const { general_permission, meeting_type_permissions, roles_data } = await this.getUserAccessPermissions(email)
        this.permissions = general_permission
        this.meetingTypePermissions = meeting_type_permissions
        this.roles_data = roles_data
      },
      async isTenantSelectionPopup(tenants) {
        let locallyStoredTenant = sessionStorage.getItem("tenantId");
        if (tenants.length > 1) {
          if (locallyStoredTenant === null || locallyStoredTenant === "null") {
            return true;
          }
          else {
            let matched = tenants.findIndex(
              (t) => t.id === locallyStoredTenant
            );
            if (matched === -1) {
              sessionStorage.removeItem("tenantId");
              localStorage.removeItem("tenantId");
              return true;
            }
            return false;
          }
        }
        else {
          sessionStorage.setItem("tenantId", tenants[0].id);
          localStorage.setItem("tenantId", tenants[0].id);
          const isLoggedIn = sessionStorage.getItem("isLoggedIn");
          if (isLoggedIn === "true") {
            await userAuthService.setActivityLog("login");
            sessionStorage.setItem("isLoggedIn", false);
          }
          return false;
        }

      },
      async updateTenantListWithHistory() {
        const tenants = this.user[process.env.VUE_APP_TENANT_KEY];
        this.user.companyTitles = await tenantConfigService.getTenantNamesHistory();
        const tenantList = await this.addDisplayNames(tenants, this.user.companyTitles)
        this.user[process.env.VUE_APP_TENANT_KEY] = tenantList
        this.user.tenant_grouping_list.tenants = tenantList;
        return tenantList
      },
      async addDisplayNames(originalList = [], nameMappings = []) {
        return new Promise((resolve) => {
          try {
            const nameMap = new Map((nameMappings || []).map(item => [item.id, item]));
            const result = (originalList || []).map(item => {
              let updatedName = item.name;
              const mappingItem = nameMap.get(item.id);
              if (mappingItem && Array.isArray(mappingItem.tenantNamesHistory) && mappingItem.tenantNamesHistory.length > 0) {
                updatedName = mappingItem.current_name;
              }
              return { ...item, name: updatedName };
            });
            resolve(result);
          } catch (err) {
            resolve(originalList || []);
          }
        });
      }
    },
    async created() {
      this.auth0Client = await createAuth0Client({
        ...options,
        client_id: options.clientId,
        redirect_uri: redirectUri,
        audience: options.audience,
        cacheLocation: 'localstorage',
        useRefreshTokens: true
      });

      try {
        if (
          window.location.search.includes("code=") &&
          window.location.search.includes("state=")
        ) {
          const { appState } = await this.auth0Client.handleRedirectCallback();
          this.error = null;
          const tenantId = sessionStorage.getItem("tenantId");
          const user = await this.auth0Client.getUser();
          const tenants = user?.[process.env.VUE_APP_TENANT_KEY] ?? [];
          if (!tenantId || tenantId === "null") {
            sessionStorage.setItem("isLoggedIn", true);
            return;
          }

          const tenantExists = tenants.some(t => t.id === tenantId);
          if (tenantExists) {
            await userAuthService.setActivityLog("login");
          }
          onRedirectCallback(appState);
        }
      } catch (e) {
        this.error = e;
      } finally {
        const isAuthenticated = await this.auth0Client.isAuthenticated();
        const user = await this.auth0Client.getUser();
        if (user && isAuthenticated) {
          let userDetails = {};
          const tenants = user[process.env.VUE_APP_TENANT_KEY];
          this.isMultipleTenant = await this.isTenantSelectionPopup(tenants);
          if (!this.isMultipleTenant) {
            const { general_permission, meeting_type_permissions, roles_data } = await this.getUserAccessPermissions(user.email)
            this.permissions = general_permission
            this.meetingTypePermissions = meeting_type_permissions
            this.roles_data = roles_data
            userDetails = await this.updateUserPermissionsDetails();
          }
          this.user = { ...user, ...userDetails };
          const hasUserDetails =
            userDetails &&
            typeof userDetails === 'object' &&
            !Array.isArray(userDetails) &&
            Object.keys(userDetails).length > 0;
          if (hasUserDetails) {
            await this.updateTenantListWithHistory();
          }
          if (userDetails.first_name && userDetails.first_name !== "") {
            this.user.tenantUserName = `${userDetails.first_name} ${userDetails.last_name}`
          }

          this.isAuthenticated = isAuthenticated
        }
        this.loading = false;
      }
    }
  });

  return instance;
};

export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options);
  }
};
